export type Portal = 'admin' | 'secretary';
export interface PortalSession<T = any> { token: string; expiresAt: number; user: T }
const key = (portal: Portal) => `muna.${portal}.session.v1`;
const eventName = (portal: Portal) => `muna:${portal}:session`;

function removeLegacy(portal: Portal) {
  for (const suffix of ['User', 'ApiKey', 'GoogleToken', 'TokenExpiry', 'Token']) {
    localStorage.removeItem(portal + suffix);
  }
}

// This is only local expiry/shape validation. The API verifies the signature.
function valid(portal: Portal, session: PortalSession): boolean {
  try {
    const parts = session.token.split('.');
    if (parts.length !== 3 || !parts[2]) return false;
    const decode = (part: string) => JSON.parse(atob(part.replace(/-/g, '+').replace(/_/g, '/')));
    const claims = decode(parts[1]);
    return decode(parts[0]).alg === 'HS256' && claims.accountType === portal
      && typeof claims.sub === 'string' && claims.sub === session.user?.email
      && Number.isInteger(claims.iat) && Number.isInteger(claims.exp)
      && claims.exp - claims.iat === 28800 && claims.iat * 1000 <= Date.now()
      && claims.exp === session.expiresAt && claims.exp * 1000 > Date.now();
  } catch { return false; }
}

export function saveSession<T>(portal: Portal, session: PortalSession<T>) {
  if (!valid(portal, session)) throw new Error('Invalid application session');
  removeLegacy(portal);
  localStorage.setItem(key(portal), JSON.stringify(session));
  window.dispatchEvent(new Event(eventName(portal)));
}

export function getSession<T = any>(portal: Portal): PortalSession<T> | null {
  removeLegacy(portal);
  const raw = localStorage.getItem(key(portal));
  if (!raw) return null;
  try {
    const session = JSON.parse(raw);
    if (valid(portal, session)) return session;
  } catch { /* Corrupt local storage must not restore a session. */ }
  localStorage.removeItem(key(portal));
  return null;
}

export function clearSession(portal: Portal, expectedToken?: string) {
  if (expectedToken !== undefined && getSession(portal)?.token !== expectedToken) return;
  removeLegacy(portal);
  localStorage.removeItem(key(portal));
  window.dispatchEvent(new Event(eventName(portal)));
}

export function watchSession<T = any>(portal: Portal, listener: (session: PortalSession<T> | null) => void) {
  let timer: ReturnType<typeof setTimeout>;
  const check = () => {
    clearTimeout(timer);
    const session = getSession<T>(portal);
    listener(session);
    if (session) timer = setTimeout(check, Math.max(0, session.expiresAt * 1000 - Date.now()));
  };
  for (const name of [eventName(portal), 'storage', 'focus']) window.addEventListener(name, check);
  check();
  return () => {
    clearTimeout(timer);
    for (const name of [eventName(portal), 'storage', 'focus']) window.removeEventListener(name, check);
  };
}

export async function portalFetch(portal: Portal, input: RequestInfo | URL, init?: RequestInit): Promise<Response> {
  const session = getSession(portal);
  if (!session) {
    clearSession(portal);
    throw new Error('Authentication required - please login again');
  }
  const headers = new Headers(init?.headers);
  headers.set('Authorization', `Bearer ${session.token}`);
  const response = await fetch(input, { ...init, headers });
  if (response.status === 401) clearSession(portal, session.token);
  return response;
}
